import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { useNavigate, useLocation } from "react-router-dom";
import { LogIn, Mail, Hash, AlertCircle, CheckCircle } from "lucide-react";
import { useSession } from "@/contexts/SessionContext";
import { applicationLogin } from "@/services/cloudFunctionService";
import * as Sentry from "@sentry/react";
import { SUPPORT_EMAIL } from "@/constants/contact";

const ApplicationLogin: React.FC = () => {
  const [applicationId, setApplicationId] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // const { session } = useSession();
  // const navigate = useNavigate();
  // const location = useLocation();

  // If user is already authenticated, redirect them
  // useEffect(() => {
  //   if (session) {
  //     const from = location.state?.from?.pathname || "/application-status";
  //     navigate(from, { replace: true });
  //   }
  // }, [session, navigate, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    // Normalize inputs: trim whitespace, email lowercase, application ID uppercase
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedApplicationId = applicationId.trim().toUpperCase();

    try {
      // Get the current origin for redirect URL - use auth-callback
      const baseRedirectUrl = `${window.location.origin}/auth-callback`;

      // Send magic link with application ID in redirect URL using our new applicationLogin function
      const result = await applicationLogin(
        normalizedEmail,
        normalizedApplicationId,
        baseRedirectUrl
      );
      const { magicLink } = result;
      if (result.success) {
        // If we have a magic link in the result, redirect directly to it
        if (magicLink) {
          // Redirect to the magic link directly
          window.location.href = magicLink;
          return; // Exit early as we're redirecting
        } else {
          setError(
            result.message || "Failed to send login link. Please try again."
          );
        }
      } else {
        setError(
          result.message || "Failed to send login link. Please try again."
        );
      }
      setIsLoading(false);
    } catch (err) {
      // Capture the error with Sentry
      Sentry.captureException(err, {
        extra: {
          context: "ApplicationLogin.handleSubmit",
          email: normalizedEmail,
          applicationId: normalizedApplicationId,
        },
      });

      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to login. Please check your credentials.";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <Card className="shadow-lg border-0">
      <CardContent className="p-6">
        {error && (
          <div className="bg-red-50 text-red-800 p-3 rounded-md mb-4 flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-50 text-green-800 p-3 rounded-md mb-4 flex items-center">
            <CheckCircle className="h-4 w-4 mr-2" />
            <span className="text-sm">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="applicationId" className="text-jungle font-medium">
              Application ID
            </Label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                id="applicationId"
                value={applicationId}
                onChange={(e) =>
                  setApplicationId(e.target.value.trim().toUpperCase())
                }
                placeholder="Enter your application ID"
                className="pl-10 border-gray-300 focus:border-honeycomb focus:ring-honeycomb"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-jungle font-medium">
              Email Address
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                placeholder="Enter your email address"
                className="pl-10 border-gray-300 focus:border-honeycomb focus:ring-honeycomb"
                required
                disabled={isLoading}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-honeycomb hover:bg-amber-500 text-white font-medium py-3 mt-6"
            disabled={isLoading || success !== null}
          >
            {isLoading ? (
              <>
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                Logging in...
              </>
            ) : success ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Logged In
              </>
            ) : (
              <>
                <LogIn className="mr-2 h-4 w-4" />
                Continue Application
              </>
            )}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex flex-col text-sm text-gray-500 pt-4 border-t border-gray-100">
        <p className="font-medium mb-1">Can't find your application ID?</p>
        <p className="text-center text-xs">
          Check your email or contact support at{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="text-honeycomb hover:text-amber-600 underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
      </CardFooter>
    </Card>
  );
};

export default ApplicationLogin;
